import { useTranslations } from '@vocab/react';
import { Box, Button, Inline, Stack } from 'braid-design-system';
import React, { useEffect } from 'react';

import { useAnalyticsContext } from '../../../modules/analytics/AnalyticsContext';
import type { SocialProvider } from '../../../types';
import { isiOSDevice } from '../../../utils/isIOS';
import { useAppContext } from '../../AppContext';

import { initiateSocialLogin } from './../../../modules/Auth0/login';
import translations from './.vocab';
import { SocialDivider } from './SocialDivider';
import getIsSocialEnabled from './getIsSocialEnabled';
import { AppleIcon } from './icons/AppleIcon';
import { FacebookIcon } from './icons/FacebookIcon';
import { GoogleIcon } from './icons/GoogleIcon';

const ProviderOAuthMap: Record<SocialProvider, string> = {
  google: 'google-oauth2',
  facebook: 'facebook',
  apple: 'apple',
};

interface RenderProviderProps {
  onClick: (provider: SocialProvider) => void;
}

const RenderSocialProvider = ({ children, label, onClick }: any) => {
  const { t } = useTranslations(translations);

  return (
    <Button tone="neutral" variant="ghost" onClick={onClick}>
      <Box
        position="absolute"
        left={0}
        marginLeft={{ mobile: 'small', tablet: 'gutter' }}
      >
        {children}
      </Box>
      <Inline space="small" align="center">
        <Box paddingLeft={{ mobile: 'small', tablet: 'none' }}>{t(label)}</Box>
      </Inline>
    </Button>
  );
};

const renderFacebook = ({ onClick }: RenderProviderProps) => (
  <RenderSocialProvider
    label="Continue with Facebook"
    key="facebook"
    onClick={() => onClick('facebook')}
  >
    <FacebookIcon height={24} width={24} />
  </RenderSocialProvider>
);

const renderGoogle = ({ onClick }: RenderProviderProps) => (
  <RenderSocialProvider
    label="Continue with Google"
    onClick={() => onClick('google')}
    key="google"
  >
    <GoogleIcon height={24} width={24} />
  </RenderSocialProvider>
);

const renderApple = ({ onClick }: RenderProviderProps) => (
  <RenderSocialProvider
    label="Continue with Apple"
    key="apple"
    onClick={() => onClick('apple')}
  >
    <AppleIcon height={24} width={24} />
  </RenderSocialProvider>
);

interface Props {
  form: 'login' | 'register';
  privacyConsent?: boolean;
}

const SocialButtons = ({ form }: Props) => {
  const {
    analytics: { trackLink },
  } = useAnalyticsContext();
  const [isSocialEnabled, setIsSocialEnabled] = React.useState<boolean>(false);
  const { site, locale } = useAppContext();

  useEffect(() => {
    const fn = () => {
      setIsSocialEnabled(getIsSocialEnabled(site, locale));
    };
    fn();
  }, [site, locale]);

  if (!isSocialEnabled) {
    return null;
  }

  const providerButtons: any[] = isiOSDevice()
    ? [renderApple, renderGoogle, renderFacebook]
    : [renderGoogle, renderFacebook, renderApple];

  const onClick = (provider: SocialProvider) => {
    trackLink({
      eventName: form === 'register' ? 'register_pressed' : 'login_pressed',
      identityProvider: provider,
    });

    initiateSocialLogin(ProviderOAuthMap[provider]);
  };

  return (
    <span data-testid="social-providers">
      <SocialDivider />
      <Stack space="small">
        {providerButtons.map((providerButton) => providerButton({ onClick }))}
      </Stack>
    </span>
  );
};

export default SocialButtons;
